import { Channel } from 'api/ChannelsAPI';
import { AxiosResponse } from 'axios';
import {
  MESSAGE_MEDIUMS,
  MESSAGE_MEDIUMS_WITH_EMBEDDED,
  MESSAGE_MEDIUM_WHATS_APP,
} from 'constants/messageMediums';
import { EspAccount, SubscriptionPolicy } from 'modules/graphql/types';
import { keyBy } from 'utils/arrayHelpers';
import { BADGE_VARIANTS } from 'ui/badge';
import { ChannelsByMessageMedium } from '../types';
import { BadgeWithLeftMargin } from '../components/ChannelsPage.styled';

export const formatChannelsByMessageMedium = (
  channels: Channel[],
  isEmbeddedOn: boolean,
  isWhatsAppOn: boolean,
): ChannelsByMessageMedium => {
  const formattedChannels = keyBy(channels, (channel: Channel) => channel.messageMedium);
  const mediumsToCheck = isEmbeddedOn ? MESSAGE_MEDIUMS_WITH_EMBEDDED : MESSAGE_MEDIUMS;
  const mediumsToCheckWithWhatsApp = isWhatsAppOn
    ? { ...mediumsToCheck, ...MESSAGE_MEDIUM_WHATS_APP }
    : mediumsToCheck;
  // add empty array if no message medium is found
  Object.values(mediumsToCheckWithWhatsApp).forEach((medium) => {
    const { dbKey } = medium;
    if (!formattedChannels[dbKey]) {
      formattedChannels[dbKey] = [];
    }
  });

  return formattedChannels;
};

export const getEspAccountById = (espAccounts: EspAccount[], espAccountId: number) =>
  espAccounts.find((espAccount) => espAccount.id === espAccountId);

export const createSubscriptionPolicyOptions = (
  isEditMode: boolean,
  isSMSDoubleOptInEnabled: boolean,
) => {
  return [
    ...(isSMSDoubleOptInEnabled
      ? [
          {
            id: 'doubleOptIn',
            description:
              'Not mandatory for GDPR but highly recommended. Users will have to confirm their subscription after inputting their phone number.',
            name: () => (
              <>
                Double opt-in{' '}
                <BadgeWithLeftMargin variant={BADGE_VARIANTS.INFO}>Recommended</BadgeWithLeftMargin>
              </>
            ),
            value: SubscriptionPolicy.DoubleOptIn,
            isDisabled: isEditMode,
          },
        ]
      : []),
    {
      id: 'optIn',
      description:
        'Users have to actively subscribe to this message type before they receive any messages. This is recommended for GDPR.',
      name: 'Opt-in',
      value: SubscriptionPolicy.OptIn,
      isDisabled: isEditMode,
    },
    {
      id: 'optOut',
      description: 'Users will automatically receive messages from this message type.',
      name: 'Opt-out',
      value: SubscriptionPolicy.OptOut,
      isDisabled: isEditMode,
    },
  ];
};

export const createMessageTypeModalHeader = (isEditMode: boolean, name: string) =>
  isEditMode ? `Edit ${name}` : 'New message type';

export const createErrorText = (baseErrorMessage: string, response: AxiosResponse) => {
  const responseData = response?.data;
  const errorText = responseData ? `${baseErrorMessage} ${responseData}` : `${baseErrorMessage}`;

  return errorText;
};

export const getDefaultSubscriptionPolicy = (
  MessageTypeSubscription: boolean,
  SMSDoubleOptIn: boolean,
) => {
  if (MessageTypeSubscription) {
    if (SMSDoubleOptIn) {
      return SubscriptionPolicy.DoubleOptIn;
    } else return SubscriptionPolicy.OptIn;
  } else {
    return SubscriptionPolicy.OptOut;
  }
};
